import React, { useState } from 'react';
import ReactDOM from 'react-dom';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Newspaper, ExternalLink, X } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import TileCard from './TileCard';
import NativePreview, { nativeParityNotes } from './NativePreview';
import { getImageUrl } from '../utils/api';
import { countEnabledSocialLinks, socialLinkSizeClasses, SOCIAL_URL_KEYS } from '../lib/socialLinks';
import { socialMediaIcons } from '../lib/socialIcons';

export default function PreviewModal({ open, onOpenChange, customer, tiles, articles = [] }) {
  const [previewGridCols, setPreviewGridCols] = useState(customer?.grid_cols || 3);
  const [mode, setMode] = useState('web');
  const parityNotes = nativeParityNotes(customer || {}, articles);
  const [selectedArticle, setSelectedArticle] = useState(null);
  const [activeChatTile, setActiveChatTile] = useState(null);
  
  // Debug: Log customer data when preview opens
  React.useEffect(() => {
    if (open) {
      console.log('=== PREVIEW MODAL RECEIVED ===');
      console.log('customer.tile_style:', customer?.tile_style);
      console.log('customer.tile_shape:', customer?.tile_shape);
      console.log('customer.tile_color:', customer?.tile_color);
      console.log('customer.primary_color:', customer?.primary_color);
      console.log('Full customer object:', customer);
      console.log('================================');
    }
  }, [open, customer]);
  
  // Update grid cols when customer data changes
  React.useEffect(() => {
    if (customer?.grid_cols) {
      setPreviewGridCols(customer.grid_cols);
    }
  }, [customer?.grid_cols, open]);

  const socialLinksSection = customer?.social_links_enabled && customer?.social_links && (() => {
    const enabledCount = countEnabledSocialLinks(customer.social_links);
    if (enabledCount === 0) return null;
    const sz = socialLinkSizeClasses(enabledCount);
    // Preview modal uses fixed sm+ sizes because it renders at 420px width.
    const buttonCls = sz.shrink ? 'w-10 h-10' : 'w-14 h-14';
    const iconCls = sz.shrink ? 'w-5 h-5' : 'w-6 h-6';
    const gapCls = sz.shrink ? 'gap-2' : 'gap-3';
    return (
      <Card className="border-none shadow-sm" style={{ backgroundColor: customer.social_background_color || '#FFFFFF' }}>
        <CardContent className="p-4 sm:p-6">
          {customer?.show_social_section_title !== false && customer?.social_section_title && (
            <h3 className="text-sm font-medium mb-4 text-center" style={{ color: customer.social_section_title_color || customer.font_color || '#1E293B' }}>
              {customer.social_section_title}
            </h3>
          )}
          <div className={`flex ${gapCls} justify-center flex-nowrap overflow-x-auto pb-2`}>
            {SOCIAL_URL_KEYS.map((platform) => {
              const url = customer.social_links[platform];
              const enabled = customer.social_links[`${platform}_enabled`];
              if (!url || !enabled) return null;
              const iconData = socialMediaIcons[platform];
              if (!iconData) return null;
              const { icon: Icon, color } = iconData;
              const label = platform === 'custom'
                ? (customer.social_links.custom_label || 'Link')
                : platform;
              return (
                <a
                  key={platform}
                  href={url}
                  target="_blank"
                  rel="noopener noreferrer"
                  title={label}
                  aria-label={label}
                  data-testid={`preview-social-link-${platform}`}
                  className={`flex items-center justify-center ${buttonCls} rounded-full bg-white shadow-md hover:shadow-lg transition-all`}
                  style={{ color }}
                >
                  <Icon className={iconCls} />
                </a>
              );
            })}
          </div>
        </CardContent>
      </Card>
    );
  })();

  const headerImageSection = customer?.header_image_url && (
    // Edge-to-edge banner. Using -mx-4 alone (no explicit width) means the
    // element's own box stays at parent width — the negative margins simply
    // pull it into the padding gutter, and the modal's overflow-x-hidden
    // still clips anything wider than the modal.
    <div className="mb-6 -mx-4 overflow-hidden">
      <img
        src={customer.header_image_url}
        alt="Header"
        className="block w-full h-auto object-contain"
      />
    </div>
  );

  const tilesSection = (
    <>
      {/* Show tiles section title only if toggle is enabled */}
      {customer?.show_tiles_section_title === true && customer?.tiles_section_title && (
        <div className="mb-6">
          <h3 className="text-base sm:text-lg font-semibold" style={{ color: customer?.tiles_section_title_color || customer?.font_color || '#1E293B' }}>
            {customer.tiles_section_title}
          </h3>
        </div>
      )}

      {tiles.length === 0 ? (
        <Card style={{ backgroundColor: customer?.tile_color || '#FFFFFF' }}>
          <CardContent className="py-12 text-center">
            <p style={{ color: customer?.font_color || '#1E293B' }}>No tiles available yet</p>
          </CardContent>
        </Card>
      ) : (
        <div className={`grid ${previewGridCols === 2 ? 'grid-cols-2' : previewGridCols === 3 ? 'grid-cols-3' : 'grid-cols-4'} gap-4`}>
          {tiles.map((tile) => (
            // min-w-0 lets the grid track actually shrink to 1fr — without it,
            // an intrinsic min-content tile can push the row wider than the
            // modal and cause the third tile to be cut off.
            <div key={tile.id} className={`min-w-0 tile-size-${customer?.tile_size || 'default'}`}>
              <TileCard 
                tile={tile}
                tileColor={customer?.tile_color}
                tileFontColor={customer?.tile_font_color}
                tileFontSize={customer?.tile_font_size}
                tileStyle={customer?.tile_style}
                tileShape={customer?.tile_shape}
                showTitle={customer?.show_tile_title !== false}
                showIcon={customer?.show_tile_icon !== false}
                primaryColor={customer?.primary_color}
                onChatOpen={(t) => setActiveChatTile(t)}
              />
            </div>
          ))}
        </div>
      )}
    </>
  );

  const newsSection = customer?.news_enabled && articles.length > 0 && (
    <Card 
      className="border-l-4" 
      style={{ 
        borderLeftColor: customer?.primary_color || '#2563EB',
        backgroundColor: customer?.tile_color || '#FFFFFF'
      }}
    >
      <CardHeader>
        <CardTitle className="flex items-center gap-2" style={{ color: customer?.primary_color || '#2563EB' }}>
          <Newspaper className="w-5 h-5" />
          Latest News
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {articles.map((article, index) => (
            <div key={article.id} className={index > 0 ? 'pt-4 border-t' : ''}>
              <div className="flex gap-4">
                {article.image_url && (
                  <img 
                    src={getImageUrl(article.image_url)} 
                    alt={article.title}
                    className="w-24 h-24 object-cover rounded flex-shrink-0"
                    onError={(e) => {
                      e.target.style.display = 'none';
                    }}
                  />
                )}
                <div className="flex-1 min-w-0">
                  <h4 className="text-lg font-semibold mb-2 line-clamp-2" style={{ color: customer?.font_color || '#1E293B' }}>
                    {article.title}
                  </h4>
                  {article.excerpt && (
                    <p className="text-sm mb-2 line-clamp-2" style={{ color: customer?.font_color || '#64748B' }}>
                      {article.excerpt}
                    </p>
                  )}
                  <Button 
                    variant="link" 
                    className="h-auto p-0 text-sm font-sans"
                    style={{ color: customer?.primary_color || '#2563EB' }}
                    onClick={() => setSelectedArticle(article)}
                  >
                    Read more <ExternalLink className="w-3 h-3 ml-1" />
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );

  const renderContent = () => {
    const position = customer?.social_links_position || 'top';
    
    if (position === 'top') {
      return (
        <div className="space-y-8">
          {headerImageSection}
          {socialLinksSection}
          {tilesSection}
          {newsSection}
        </div>
      );
    } else if (position === 'between') {
      return (
        <div className="space-y-8">
          {headerImageSection}
          {tilesSection}
          {socialLinksSection}
          {newsSection}
        </div>
      );
    } else {
      return (
        <div className="space-y-8">
          {headerImageSection}
          {tilesSection}
          {newsSection}
          {socialLinksSection}
        </div>
      );
    }
  };

  return (
    <>
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-[420px] max-w-[min(420px,calc(100vw-2rem))] sm:max-w-[420px] max-h-[90vh] overflow-y-auto overflow-x-hidden p-0">
        <DialogHeader className="px-4 pt-4">
          <DialogTitle>{mode === 'native' ? 'Native App Preview' : 'Mobile Preview'}</DialogTitle>
          <p className="text-sm text-muted-foreground">
            {mode === 'native'
              ? 'How the iOS app renders your settings'
              : 'Exactly how your users will see it'}
          </p>
          <div className="flex gap-1 p-1 bg-muted rounded-lg mt-2" data-testid="preview-mode-toggle">
            <button
              onClick={() => setMode('web')}
              className={`flex-1 text-xs font-medium py-1.5 rounded-md transition-colors ${mode === 'web' ? 'bg-white shadow-sm' : 'text-muted-foreground'}`}
              data-testid="preview-mode-web"
            >
              Web / PWA
            </button>
            <button
              onClick={() => setMode('native')}
              className={`flex-1 text-xs font-medium py-1.5 rounded-md transition-colors ${mode === 'native' ? 'bg-white shadow-sm' : 'text-muted-foreground'}`}
              data-testid="preview-mode-native"
            >
              Native app
            </button>
          </div>
          {mode === 'native' && parityNotes.length > 0 && (
            <div
              className="mt-2 rounded-md border border-amber-200 bg-amber-50 p-2.5 text-left"
              data-testid="preview-parity-notes"
            >
              <p className="text-xs font-semibold text-amber-900 mb-1">Differences in the app</p>
              <ul className="space-y-1">
                {parityNotes.map((note, i) => (
                  <li key={i} className="text-xs text-amber-800 leading-snug">• {note}</li>
                ))}
              </ul>
            </div>
          )}
        </DialogHeader>

        {mode === 'native' ? (
          <NativePreview customer={customer} tiles={tiles} articles={articles} />
        ) : (
        <div 
          className="px-4 pb-4 rounded-lg overflow-x-hidden"
          style={{ 
            backgroundColor: customer?.background_color || '#F8FAFC',
            fontFamily: customer?.font_family || 'Inter'
          }}
        >
          {customer?.show_header_text !== false && (
            <div className={`mb-8 min-w-0 ${customer?.header_text_align === 'center' ? 'text-center' : customer?.header_text_align === 'right' ? 'text-right' : 'text-left'}`}>
              <h1 
                className={`font-bold break-words ${
                  customer?.header_text_size === 'sm' ? 'text-lg' :
                  customer?.header_text_size === 'base' ? 'text-xl' :
                  customer?.header_text_size === 'lg' ? 'text-2xl' :
                  customer?.header_text_size === '2xl' ? 'text-3xl' :
                  customer?.header_text_size === '3xl' ? 'text-4xl' :
                  'text-3xl'
                }`}
                style={{ 
                  color: customer?.header_text_color || customer?.primary_color || '#2563EB',
                  fontFamily: customer?.header_text_font || customer?.font_family || 'Inter'
                }}
              >
                {customer?.header_text || 'Welcome to Taptile'}
              </h1>
            </div>
          )}
          
          {renderContent()}
        </div>
        )}
      </DialogContent>

      {/* Article Detail Dialog */}
      <Dialog open={!!selectedArticle} onOpenChange={(open) => !open && setSelectedArticle(null)}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-2xl">{selectedArticle?.title}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {selectedArticle?.image_url && (
              <img 
                src={getImageUrl(selectedArticle.image_url)} 
                alt={selectedArticle.title}
                className="w-full h-64 object-cover rounded-lg"
              />
            )}
            {selectedArticle?.excerpt && (
              <p className="text-lg text-muted-foreground italic border-l-4 border-primary pl-4">
                {selectedArticle.excerpt}
              </p>
            )}
            <div className="max-w-none">
              {selectedArticle?.content.split('\n').map((paragraph, index) => (
                <p key={index} className="mb-4 text-foreground leading-relaxed">{paragraph}</p>
              ))}
            </div>
            <div className="text-sm text-muted-foreground pt-4 border-t">
              By {selectedArticle?.author} • {selectedArticle?.created_at && new Date(selectedArticle.created_at).toLocaleDateString()}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </Dialog>

    {/* Chat overlay — portal so it renders above the modal */}
    {activeChatTile && ReactDOM.createPortal((() => {
      const raw = activeChatTile.embed_code || activeChatTile.url || '';
      const match = raw.match(/ubots\.chat\/chat\/([a-f0-9-]{36})/i);
      const embedId = match ? match[1] : raw.replace('https://ubots.chat/chat/', '').trim();
      const chatUrl = `https://ubots.chat/chat/${embedId}`;
      return (
        <div className="fixed inset-0 flex flex-col" style={{ zIndex: 9999, backgroundColor: customer?.primary_color || '#2563EB' }}>
          <div className="flex items-center justify-between px-4 py-3 flex-shrink-0">
            <span className="text-white font-semibold text-base truncate">{activeChatTile.title || 'Chat'}</span>
            <button onClick={() => setActiveChatTile(null)} className="text-white opacity-80 hover:opacity-100 p-1 rounded-full" aria-label="Close chat">
              <X className="w-6 h-6" />
            </button>
          </div>
          <iframe src={chatUrl} title={activeChatTile.title || 'Chat'} className="flex-1 w-full border-0 bg-white" allow="microphone; camera" />
        </div>
      );
    })(), document.body)}
    </>
  );
}